import axios from "axios";

import React, { useContext, useState, useEffect, createContext } from "react";
import { signOut as firebaseSignOut } from "firebase/auth";
import { auth } from "../../service/firebase/config";
import { useHistory, useLocation } from "react-router-dom";
import { getHeaders } from "../../service/api_client";
import {
  GoogleLoginApi,
  LogoutApi,
  VerifyEmailTokenAPI,
} from "../../service/repos/auth_repo";
import { ACTION } from "../../utils/enums";
import LoadingWidget from "../modal/loader";
import { ToastType, useToast } from "../toast/toast_ctx";
import { UserModel } from "./user_model";

interface authCtx {
  isAuthorised: ({
    action,
    module,
    operator,
  }: {
    module?: string;
    action?: string | string[];
    operator?: "or" | "and" | undefined;
  }) => boolean;
  user?: UserModel;
  logout: () => Promise<void>;
  google_login: (idToken: string) => Promise<void>;
  setUser: React.Dispatch<React.SetStateAction<UserModel | undefined>>;
  backdatedPOCreationAuthorised: ({
    action,
    module,
  }: {
    module?: string;
    action?: string | string[];
  }) => boolean;
  hasRoleTitle: ({
    roleTitle,
    action,
  }: {
    roleTitle: string;
    action?: string | string[];
  }) => boolean;
  gstUpdateAuthorised: ({
    action,
    module,
  }: {
    module?: string;
    action?: string | string[];
  }) => boolean;
}

const defaultValue: authCtx = {
  isAuthorised: function ({
    action,
    module,
    operator,
  }: {
    module?: string;
    action?: string | string[];
    operator?: "or" | "and" | undefined;
  }): boolean {
    throw new Error("Function not implemented.");
  },
  logout: function (): Promise<void> {
    throw new Error("Function not implemented.");
  },
  google_login: function (idToken: string): Promise<void> {
    throw new Error("Function not implemented.");
  },
  setUser: function (value: React.SetStateAction<UserModel | undefined>): void {
    throw new Error("Function not implemented.");
  },
  backdatedPOCreationAuthorised: function ({
    action,
    module,
  }: {
    module?: string;
    action?: string | string[];
  }): boolean {
    throw new Error("Function not implemented.");
  },

  hasRoleTitle: function ({
    roleTitle,
    action,
  }: {
    roleTitle: string;
    action?: string | string[];
  }): boolean {
    throw new Error("Function not implemented.");
  },
  gstUpdateAuthorised: function ({
    action,
    module,
  }: {
    module?: string;
    action?: string | string[];
  }): boolean {
    throw new Error("Function not implemented.");
  },
};

const AuthContext = createContext<authCtx>(defaultValue);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: any }) {
  const history = useHistory();
  let location = useLocation();

  const { showToast } = useToast();
  const [user, setUser] = useState<UserModel>();
  // const { setLoading } = useModal();
  const [loading, setLoading] = useState(true);

  // Check if JWT token is valid (not expired)
  const isTokenValid = (token: string | null): boolean => {
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const now = Math.floor(Date.now() / 1000);

      if (!payload.exp) return false;

      return payload.exp > now; // true if token not expired
    } catch (err) {
      return false; // token malformed
    }
  };

  // Clear session without calling logout API (for expired tokens)
  const clearSession = () => {
    firebaseSignOut(auth);
    setUser(undefined);

    const preserveKey = "NEVER_INSTALL";
    Object.keys(localStorage).forEach((key) => {
      if (key !== preserveKey) {
        localStorage.removeItem(key);
      }
    });
  };

  async function google_login(idToken: string) {
    setLoading(true);
    const res = await GoogleLoginApi({ idToken });

    if (res.success) {
      setUser(res.data);
      window.localStorage.setItem("token", res.data.token);
    } else {
      showToast({ type: ToastType.error, text: res.error });
      firebaseSignOut(auth);
    }

    setLoading(false);
  }

  async function logout() {
    setLoading(true);
    const res = await LogoutApi();

    if (res.success) {
      await firebaseSignOut(auth);
      setUser(undefined);

      const preserveKey = "NEVER_INSTALL";
      Object.keys(localStorage).forEach((key) => {
        if (key !== preserveKey) {
          localStorage.removeItem(key);
        }
      });

      showToast({ type: ToastType.success, text: res.message });
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  }
  async function getUserData() {
    setLoading(true);
    try {
      // const res = await GetAuthDataApi();

      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/auth/data`;
      const res = (
        await axios.get(url, {
          withCredentials: true,
          headers: { ...getHeaders() },
        })
      ).data;

      if (res.success) {
        setUser(res.data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      const { message, response, request } = error;

      if (response && response.status === 401) {
        firebaseSignOut(auth);
        window.localStorage.removeItem("token");
      }
      // else if (response && response.status === 400) return response.data;
      // else if (response && response.status === 403) return response.data;
    }
    setLoading(false);
  }

  useEffect(() => {
    const initAuth = async () => {
      const query = new URLSearchParams(location.search);
      const urlToken = query.get("token");

      // 1️⃣ Email login flow
      if (urlToken) {
        try {
          const res = await VerifyEmailTokenAPI(urlToken);
          if (res.success) {
            localStorage.setItem("token", res.data.token);
            query.delete("token");
            history.replace(location.pathname);
          } else {
            showToast({ type: ToastType.error, text: res.error });
            clearSession();
          }
        } catch (e) {
          clearSession();
        }
      }

      // 2️⃣ Normal session bootstrap
      const storedToken = localStorage.getItem("token");

      if (!storedToken || !isTokenValid(storedToken)) {
        clearSession();
        setLoading(false);
        return;
      }

      // 3️⃣ Fetch user if needed
      if (!user) {
        await getUserData();
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const isAuthorised = ({
    action,
    module,
    operator,
  }: {
    module?: string;
    action?: string | string[];
    operator?: "or" | "and" | undefined;
  }) => {
    if (!action || action.length === 0 || !module) return true;
    const roles = user?.emp_profile?.permissions ?? [];
    if (roles.length > 0) {
      let flag = false;
      for (let i = 0; i < roles.length; i++) {
        const role = roles[i];
        for (let j = 0; j < role.permissions.length; j++) {
          const per = role.permissions[j];
          if (module === per.module) {
            // matched mod

            // check for actions

            if (typeof action === "string") {
              if (
                per.action.indexOf(action) !== -1 ||
                per.action.indexOf(ACTION.ALL as any) !== -1
              )
                return true;
            } else {
              for (let i = 0; i < action.length; i++) {
                const act = action[i];

                if (!operator || operator === "or") {
                  if (
                    per.action.indexOf(act) !== -1 ||
                    per.action.indexOf(ACTION.ALL as any) !== -1
                  ) {
                    flag = true;
                    break;
                  }
                } else {
                  if (
                    per.action.indexOf(act) !== -1 ||
                    per.action.indexOf(ACTION.ALL as any) !== -1
                  ) {
                    flag = true;
                  } else {
                    flag = false;
                    break;
                  }
                }
              }
            }
          }
        }
        if (flag) break;
      }
      return flag;
    }
    return false;
  };
  const backdatedPOCreationAuthorised = ({
    action,
    module,
  }: {
    module?: string;
    action?: string | string[];
  }) => {
    if (!action || action.length === 0 || !module) return true;
    const roles = user?.emp_profile?.permissions ?? [];
    if (roles.length > 0) {
      for (let i = 0; i < roles.length; i++) {
        let roleTitle = roles[i].title.toLowerCase().trim();
        // console.log("1122 role title : ", roles[i]?.title, roleTitle);

        if (roleTitle === "backdated_po_creation") {
          console.log("1122  returning true", roles[i].title, roleTitle);
          return true;
        }
      }
    }
    return false;
  };

  const hasRoleTitle = ({ roleTitle }: { roleTitle: string }) => {
    if (!roleTitle) return false;

    const normalizedRoleTitle = roleTitle.toLowerCase().trim();
    const roles = user?.emp_profile?.permissions ?? [];
    if (roles.length > 0) {
      for (let i = 0; i < roles.length; i++) {
        const currentRoleTitle = roles[i].title.toLowerCase().trim();

        if (currentRoleTitle === normalizedRoleTitle) {
          return true;
        }
      }
    }
    return false;
  };

  const gstUpdateAuthorised = ({
    action,
    module,
  }: {
    module?: string;
    action?: string | string[];
  }) => {
    if (!action || action.length === 0 || !module) return true;
    const roles = user?.emp_profile?.permissions ?? [];
    if (roles.length > 0) {
      for (let i = 0; i < roles.length; i++) {
        let roleTitle = roles[i].title.toLowerCase();
        // console.log("role title : ", roles[i]?.title);

        if (roleTitle.toLowerCase().trim() === "vendor_gst_update") {
          console.log(" 1122 roles title", roles[i].title);
          return true;
        }
      }
    }
    return false;
  };

  const value = {
    user,
    google_login,
    logout,
    setUser,
    isAuthorised,
    backdatedPOCreationAuthorised,
    gstUpdateAuthorised,
    hasRoleTitle,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <LoadingWidget />}
    </AuthContext.Provider>
  );
}
